<template>
  <div class="menu-config-container">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2 class="page-title">
        <a-icon type="menu" />
        菜单配置
      </h2>
      <p class="page-description">管理系统菜单和导航结构</p>
    </div>

    <!-- 操作按钮 -->
    <div class="action-bar">
      <a-button type="primary" @click="addMenu">
        <a-icon type="plus" />
        添加菜单
      </a-button>
      <a-button @click="refreshMenus">
        <a-icon type="reload" />
        刷新
      </a-button>
    </div>

    <!-- 菜单列表 -->
    <div class="menu-list">
      <a-table
        :columns="columns"
        :data-source="menus"
        :loading="loading"
        :pagination="false"
        row-key="id"
        size="middle"
      >
        <!-- 菜单名称列 -->
        <template slot="name" slot-scope="text, record">
          <div class="menu-name">
            <a-icon :type="record.icon || 'menu'" />
            <span>{{ text }}</span>
          </div>
        </template>

        <!-- 路径列 -->
        <template slot="path" slot-scope="text">
          <a-tag color="blue">{{ text }}</a-tag>
        </template>

        <!-- 状态列 -->
        <template slot="status" slot-scope="text">
          <a-tag :color="text ? 'green' : 'red'">
            {{ text ? '启用' : '禁用' }}
          </a-tag>
        </template>

        <!-- 操作列 -->
        <template slot="action" slot-scope="text, record">
          <a-button-group size="small">
            <a-button @click="editMenu(record)">
              <a-icon type="edit" />
              编辑
            </a-button>
            <a-button @click="deleteMenu(record)" type="danger">
              <a-icon type="delete" />
              删除
            </a-button>
          </a-button-group>
        </template>
      </a-table>
    </div>

    <!-- 添加/编辑菜单弹窗 -->
    <a-modal
      :title="modalTitle"
      :visible="modalVisible"
      :confirm-loading="confirmLoading"
      @ok="handleModalOk"
      @cancel="handleModalCancel"
      width="600px"
    >
      <a-form :form="form" layout="vertical">
        <a-form-item label="菜单名称">
          <a-input
            v-decorator="['name', { rules: [{ required: true, message: '请输入菜单名称' }] }]"
            placeholder="请输入菜单名称"
          />
        </a-form-item>
        
        <a-form-item label="菜单路径">
          <a-input
            v-decorator="['path', { rules: [{ required: true, message: '请输入菜单路径' }] }]"
            placeholder="请输入菜单路径，如：/dashboard"
          />
        </a-form-item>
        
        <a-form-item label="菜单图标">
          <a-input
            v-decorator="['icon', { rules: [{ required: true, message: '请输入菜单图标' }] }]"
            placeholder="请输入图标名称，如：home、user、menu"
          />
        </a-form-item>
        
        <a-form-item label="菜单描述">
          <a-textarea
            v-decorator="['description']"
            placeholder="请输入菜单描述"
            :rows="3"
          />
        </a-form-item>
        
        <a-form-item label="状态">
          <a-switch
            v-decorator="['status', { valuePropName: 'checked', initialValue: true }]"
            checked-children="启用"
            un-checked-children="禁用"
          />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
export default {
  name: 'MenuConfig',
  data() {
    return {
      loading: false,
      menus: [],
      modalVisible: false,
      modalTitle: '添加菜单',
      confirmLoading: false,
      currentMenu: null,
      form: this.$form.createForm(this),
      columns: [
        {
          title: '菜单名称',
          dataIndex: 'name',
          key: 'name',
          scopedSlots: { customRender: 'name' },
          width: 200,
        },
        {
          title: '路径',
          dataIndex: 'path',
          key: 'path',
          scopedSlots: { customRender: 'path' },
          width: 150,
        },
        {
          title: '图标',
          dataIndex: 'icon',
          key: 'icon',
          width: 100,
        },
        {
          title: '描述',
          dataIndex: 'description',
          key: 'description',
          ellipsis: true,
        },
        {
          title: '状态',
          dataIndex: 'status',
          key: 'status',
          scopedSlots: { customRender: 'status' },
          width: 100,
        },
        {
          title: '操作',
          key: 'action',
          scopedSlots: { customRender: 'action' },
          width: 150,
          align: 'center',
        },
      ],
    };
  },
  mounted() {
    this.loadMenus();
  },
  methods: {
    // 加载菜单列表
    loadMenus() {
      this.loading = true;
      // 从store中获取当前菜单数据
      const storeMenus = this.$store.state.menus || [];
      this.menus = storeMenus.map(menu => ({
        ...menu,
        status: true, // 默认启用
        description: menu.description || '系统菜单'
      }));
      this.loading = false;
      console.log('加载的菜单数据:', this.menus);
    },

    // 刷新菜单
    refreshMenus() {
      this.loadMenus();
      this.$message.success('菜单列表已刷新');
    },

    // 添加菜单
    addMenu() {
      this.modalTitle = '添加菜单';
      this.currentMenu = null;
      this.modalVisible = true;
      this.form.resetFields();
    },

    // 编辑菜单
    editMenu(record) {
      this.modalTitle = '编辑菜单';
      this.currentMenu = record;
      this.modalVisible = true;
      this.$nextTick(() => {
        this.form.setFieldsValue({
          name: record.name,
          path: record.path,
          icon: record.icon,
          description: record.description,
          status: record.status,
        });
      });
    },

    // 删除菜单
    deleteMenu(record) {
      this.$confirm({
        title: '确认删除',
        content: `确定要删除菜单"${record.name}"吗？`,
        onOk: () => {
          // 从菜单列表中移除
          const index = this.menus.findIndex(menu => menu.id === record.id);
          if (index > -1) {
            this.menus.splice(index, 1);
            this.updateStoreMenus();
            this.$message.success('菜单删除成功');
          }
        },
      });
    },

    // 弹窗确认
    handleModalOk() {
      this.form.validateFields((err, values) => {
        if (!err) {
          this.confirmLoading = true;
          
          if (this.currentMenu) {
            // 编辑菜单
            const index = this.menus.findIndex(menu => menu.id === this.currentMenu.id);
            if (index > -1) {
              this.menus[index] = {
                ...this.menus[index],
                ...values,
                status: values.status
              };
            }
            this.$message.success('菜单更新成功');
          } else {
            // 添加菜单
            const newMenu = {
              id: Date.now(), // 简单的ID生成
              ...values,
              status: values.status
            };
            this.menus.push(newMenu);
            this.$message.success('菜单添加成功');
          }
          
          this.updateStoreMenus();
          this.confirmLoading = false;
          this.modalVisible = false;
        }
      });
    },

    // 弹窗取消
    handleModalCancel() {
      this.modalVisible = false;
      this.currentMenu = null;
      this.form.resetFields();
    },

    // 更新store中的菜单数据
    updateStoreMenus() {
      const updatedMenus = this.menus.map(menu => ({
        id: menu.id,
        name: menu.name,
        path: menu.path,
        icon: menu.icon
      }));
      this.$store.commit('setMenus', updatedMenus);
      console.log('更新后的菜单数据:', updatedMenus);
    },
  },
};
</script>

<style scoped>
.menu-config-container {
  padding: 24px;
  background: #f5f7fa;
  min-height: 100vh;
}

.page-header {
  margin-bottom: 24px;
}

.page-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 24px;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 8px 0;
}

.page-description {
  color: #718096;
  font-size: 14px;
  margin: 0;
}

.action-bar {
  margin-bottom: 16px;
  display: flex;
  gap: 12px;
}

.menu-list {
  background: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.menu-name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
}

.menu-name i {
  color: #3182ce;
}

/* 表格样式优化 */
.menu-list >>> .ant-table-thead > tr > th {
  background: #f7fafc;
  font-weight: 600;
  color: #2d3748;
}

.menu-list >>> .ant-table-tbody > tr:hover > td {
  background: #f7fafc;
}

/* 按钮组样式 */
.menu-list >>> .ant-btn-group .ant-btn {
  border-radius: 4px;
}

.menu-list >>> .ant-btn-group .ant-btn:first-child {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.menu-list >>> .ant-btn-group .ant-btn:last-child {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-left: 0;
}
</style>
